// src/features/task/form/TaskFormComposer.tsx
// The task form itself — every row, every section, every message.
//
// Two columns, split by who does the work. The left column is what a person
// *writes*: the key the task will get, the title, the description, and the
// secondary blocks (checklists, watchers, attachments, custom fields) waiting
// behind an add-action. The right column is what a person *chooses*: an
// always-open panel with one labelled row per property, so nothing about the
// task is hidden behind a pill you have to think to click.
//
// A panel row never disappears. An unset property reads "not set" in muted
// text, a required one is marked before the button is ever pressed, and the
// footer says how many required fields are still missing. A source that came
// back empty still leaves its row — it simply has nothing to offer inside.
//
// This component is the answer to "why does editing look different from
// creating?": there is one form, and both pages render it. What differs is
// what the surrounding page says (heading, action bar) and what the hook does
// on submit — never the shape of the form. The two mode-dependent bits here
// earn their keep in comments right where they sit.
//
// Nothing about the system is explained on the page. The parent scope decides
// which types, templates, categories and numbering apply (via
// `ResolveTaskContext`), and that sentence is said once, inside the parent's
// own popover, where it is an answer instead of a lecture.

import { Fragment, useEffect } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Alert, ErrorAlert } from '@react/components';
import { cn } from '@core/ui';
import type { FormPropertySlot } from '@/features/task/formProperties';
import type { CustomFieldDefinition } from '@core/types/task';

import { TagPicker } from '@/components/TagPicker';
import { UserPicker } from '@/components/UserPicker';
import { EntityAvatar } from '@/components/EntityAvatar';
import { AutoGrowTextarea, TitleInput } from '@/components/form';

import {
  getEntityId,
  getPriorityColor,
  getPriorityIconPath,
  taskPriorityOptions,
} from '@/features/task/logic';

import { AddAction, FormSection } from './AddAction';
import { AttachmentDropzone } from './AttachmentDropzone';
import { ChecklistDraftEditor } from './ChecklistDraftEditor';
import { CustomFieldControl } from './CustomFieldControl';
import { FORM_ICONS, Icon } from './icons';
import { PillOptionList, PropertyPill } from './PropertyPill';
import { PropertyRow } from './PropertyRow';
import { TaskTypeSelect } from './TaskTypeSelect';
import { customFieldId, typeDefId } from './logic';
import type { ParentKind } from './scope';
import {
  SECTION_ATTACHMENTS,
  SECTION_CHECKLISTS,
  SECTION_WATCHERS,
  customFieldSectionKey,
  type TaskFormHandle,
} from './useTaskForm';

/**
 * True when the custom field renders a single labelable control carrying the id.
 * The user picker and the chip multi-select render composites instead, so
 * pointing a `<label for>` at them would be a dangling reference — they carry
 * their own accessible name.
 */
function labelableCustomField(def: CustomFieldDefinition): boolean {
  return (
    def.field_type !== 'CUSTOM_FIELD_TYPE_USER' &&
    def.field_type !== 'CUSTOM_FIELD_TYPE_MULTI_SELECT'
  );
}

export interface TaskFormComposerProps {
  /** The form state and rules — from `useTaskForm`. */
  form: TaskFormHandle;
  /** Prefix for the DOM ids this form mints. */
  idPrefix?: string;
  /**
   * The action bar. Each surface words its own primary action ("Create" and
   * "Save changes" are different promises), so the bar is passed in rather
   * than guessed at from the mode.
   */
  actions?: ReactNode;
}

export function TaskFormComposer({ form: f, idPrefix = 'task-form', actions }: TaskFormComposerProps) {
  const { t } = useI18n();

  const {
    store,
    isEdit,
    customFieldsLoading,
    descriptionRef,
    formRef,
    touched,
    titleFieldId,
    parentKind,
    setParentKind,
    parentId,
    setParentId,
    otherTypeName,
    setOtherTypeName,
    parentKinds,
    parentOptions,
    parentIsLoading,
    parentLoadError,
    retryParents,
    parentValue,
    parentDisplayName,
    pickerScope,
    form,
    setField,
    setAssignee,
    errors,
    errorText,
    isSubmitting,
    submit,
    submitNow,
    availableTypes,
    selectedType,
    setSelectedTypeId,
    typeLabel,
    typeDropped,
    categories,
    categoryId,
    setCategoryId,
    categoryValue,
    capabilities,
    priorityValue,
    priorityIcon,
    priorityIconClass,
    tags,
    setTags,
    tagsValue,
    formatDate,
    contextLoading,
    contextError,
    retryContext,
    applyTemplate,
    numberingScheme,
    keyPreview,
    customFieldGroups,
    formSlots,
    panelCustomFields,
    cfValues,
    setCustomField,
    filledInGroup,
    checklists,
    setChecklists,
    checklistItemCount,
    watchers,
    setWatchers,
    pendingFiles,
    setPendingFiles,
    handleAddFiles,
    uploadWarning,
    advancedPreset,
    sectionOpen,
    toggleSection,
    descriptionPreview,
    setDescriptionPreview,
    templateDropped,
  } = f;

  // ⌘/Ctrl+Enter submits from anywhere in the form. Escape is deliberately not
  // bound — it must never throw away what is being typed.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        void submitNow();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [submitNow]);

  const notSet = t('common.notSet');

  const dateInputClass =
    'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground ' +
    'focus:outline-none focus:ring-2 focus:ring-ring';

  const clearButtonClass =
    'self-start rounded-sm text-xs text-muted-foreground underline-offset-2 ' +
    'hover:text-destructive hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  // ── The screen, block by block, in the order it is read. ───────────────

  // The title: the loudest thing here, and where the caret lands on mount.
  const titleBlock: ReactNode = (
    <div data-field-error={errors.title ? 'true' : undefined}>
      {keyPreview && (
        <p
          className="mb-1 font-mono text-xs text-muted-foreground"
          title={numberingScheme?.name || t('task.createForm.keyPreview')}
        >
          {keyPreview}
        </p>
      )}
      <TitleInput
        id={titleFieldId}
        size="lg"
        value={form.title}
        onChange={(value) => setField('title', value)}
        placeholder={t('task.createForm.titlePlaceholder')}
        initialFocus
        aria-label={t('task.field.title')}
        onEnter={() => descriptionRef.current?.focus()}
      />
      {errors.title && (
        <p role="alert" className="mt-1 text-xs text-destructive">
          {errorText('title')}
        </p>
      )}
    </div>
  );

  // The description: borderless and auto-growing, with one named switch above
  // it. The switch is always there — an icon that appears only once something
  // is typed is a control nobody finds, which is exactly how "there is no
  // preview" happens. It stays a single labelled button rather than a
  // full-width Write/Preview band: the band costs a whole row to say what one
  // word says.
  const descriptionBlock: ReactNode = (
    <div>
      <div className="mb-1 flex justify-end">
        <button
          type="button"
          onClick={() => setDescriptionPreview((prev) => !prev)}
          aria-pressed={descriptionPreview}
          aria-controls={`${idPrefix}-description`}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors',
            'hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            descriptionPreview ? 'bg-muted text-foreground' : 'text-muted-foreground',
          )}
        >
          <Icon
            d={descriptionPreview ? FORM_ICONS.pencil : FORM_ICONS.eye}
            className="h-3.5 w-3.5"
          />
          {descriptionPreview ? t('task.write') : t('task.preview')}
        </button>
      </div>
      {descriptionPreview ? (
        <div
          id={`${idPrefix}-description`}
          className="prose prose-sm min-h-16 max-w-none text-foreground dark:prose-invert"
        >
          {form.description.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.description}</ReactMarkdown>
          ) : (
            <p className="text-sm text-muted-foreground/60">{t('task.previewNoDescription')}</p>
          )}
        </div>
      ) : (
        <AutoGrowTextarea
          ref={descriptionRef}
          id={`${idPrefix}-description`}
          value={form.description}
          onChange={(value) => setField('description', value)}
          placeholder={t('task.createForm.descriptionPlaceholder')}
          aria-label={t('task.field.description')}
          minRows={3}
        />
      )}
    </div>
  );

  // ─── The properties panel ───────────────────────────────────────────────
  // Always open, one labelled row per property, in the order people ask the
  // questions: where does it live, what is it, how urgent, who, when, how big.

  // The parent is chosen once, when the task is filed. `UpdateTask` has no
  // field-mask path for it, so on an existing task the row shows where the
  // task lives — and says why it cannot be moved from here — instead of
  // offering a change that would be silently dropped.
  // Each row is a function of its slot rather than a constant, because the same
  // property is required under one type and optional under the next.

  /** DOM id for a row's validation message. `cf:` is not id-safe; `-` is. */
  const rowErrorId = (slot: FormPropertySlot) =>
    `task-form-error-${slot.key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  const parentRow = (slot: FormPropertySlot): ReactNode => (
    <PropertyRow
      label={t('task.createForm.pill.parent')}
      required={slot.required}
      error={errorText('parent')}
      errorId={rowErrorId(slot)}
    >
      <PropertyPill
        variant="row"
        icon={FORM_ICONS.folder}
        label={t('task.createForm.pill.parent')}
        value={parentValue}
        emptyText={notSet}
        required={slot.required}
        invalid={Boolean(errors.parent)}
        errorId={rowErrorId(slot)}
        disabled={isSubmitting}
        popoverTitle={t('task.field.parent')}
        popoverHint={t('task.createForm.parentDrivesForm', { parent: parentDisplayName })}
        popoverWidth={320}
      >
        {() => (
          <div className="flex flex-col gap-3">
            <PillOptionList
              ariaLabel={t('task.createForm.parentType')}
              value={parentKind}
              options={parentKinds.map((kind) => ({
                value: kind,
                label: t(`task.createForm.parent.${kind}`),
              }))}
              onSelect={(kind) => {
                setParentKind(kind as ParentKind);
                setParentId('');
                applyTemplate(null);
              }}
            />

            {parentKind === 'entity' && (
              <div className="flex flex-col gap-2 border-t border-border pt-2">
                <label
                  htmlFor="task-create-parent-type"
                  className="text-xs text-muted-foreground"
                >
                  {t('task.createForm.parentEntityType')}
                </label>
                <input
                  id="task-create-parent-type"
                  type="text"
                  value={otherTypeName}
                  onChange={(e) => setOtherTypeName(e.target.value)}
                  placeholder={t('task.createForm.parentEntityTypePlaceholder')}
                  className={dateInputClass}
                />
                <label htmlFor="task-create-parent-id" className="text-xs text-muted-foreground">
                  {t('task.field.parent')}
                </label>
                <input
                  id="task-create-parent-id"
                  type="text"
                  value={parentId}
                  onChange={(e) => {
                    setParentId(e.target.value);
                    applyTemplate(null);
                  }}
                  placeholder={t('task.createForm.entityIdPlaceholder')}
                  className={`${dateInputClass} font-mono placeholder:font-sans`}
                />
              </div>
            )}

            {(parentKind === 'project' || parentKind === 'team') && (
              <div className="border-t border-border pt-2">
                {parentIsLoading ? (
                  <div
                    className="h-8 w-full motion-safe:animate-pulse rounded-md bg-muted"
                    aria-busy="true"
                  />
                ) : parentLoadError ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs text-destructive">{t('task.createForm.parentLoadFailed')}</p>
                    <button
                      type="button"
                      onClick={retryParents}
                      className="rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {t('common.retry')}
                    </button>
                  </div>
                ) : parentOptions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {t('task.createForm.noParentOptions')}
                  </p>
                ) : (
                  <PillOptionList
                    ariaLabel={t('task.field.parent')}
                    value={parentId}
                    options={parentOptions}
                    onSelect={(value) => {
                      setParentId(value);
                      applyTemplate(null);
                    }}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </PropertyPill>
    </PropertyRow>
  );

  // Two things at once: the control is the list itself, so picking a type is a
  // single click rather than "open a popover, then find the picker in it"; and
  // no configuration may remove this row, since without it the type cannot be
  // changed and with it goes the only way back to a complete form.
  const typeRow = (): ReactNode => (
    <PropertyRow label={t('task.createForm.pill.type')}>
      <TaskTypeSelect
        variant="row"
        label={t('task.field.type')}
        emptyText={notSet}
        types={availableTypes}
        value={typeDefId(selectedType)}
        onChange={(id) => {
          touched.current.add('type');
          setSelectedTypeId(id);
        }}
        isLoading={contextLoading}
        error={contextError}
        onRetry={() => retryContext()}
        disabled={isSubmitting}
        manageHref="/settings/task-types"
      />
    </PropertyRow>
  );

  const categoryRow = (slot: FormPropertySlot): ReactNode => (
    <PropertyRow
      label={t('task.createForm.pill.category')}
      required={slot.required}
      error={errorText('category')}
      errorId={rowErrorId(slot)}
    >
      <PropertyPill
        variant="row"
        icon={FORM_ICONS.grid}
        label={t('task.createForm.pill.category')}
        value={categoryValue}
        emptyText={notSet}
        required={slot.required}
        invalid={Boolean(errors.category)}
        errorId={rowErrorId(slot)}
        disabled={isSubmitting}
        popoverTitle={t('task.categories.title')}
      >
        {(close) =>
          contextLoading && categories.length === 0 ? (
            <div
              className="h-8 w-full motion-safe:animate-pulse rounded-md bg-muted"
              aria-busy="true"
            />
          ) : contextError && categories.length === 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs text-destructive">{t('task.createForm.contextError')}</p>
              <button
                type="button"
                onClick={() => retryContext()}
                className="rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('common.retry')}
              </button>
            </div>
          ) : categories.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('task.createForm.noCategories')}</p>
          ) : (
            <PillOptionList
              ariaLabel={t('task.categories.title')}
              value={categoryId}
              options={[
                { value: '', label: t('task.createForm.noCategory') },
                ...categories.map((category) => ({
                  value: getEntityId(category),
                  label: category.name,
                  hint: category.code || undefined,
                })),
              ]}
              onSelect={(value) => {
                setCategoryId(value);
                close();
              }}
            />
          )
        }
      </PropertyPill>
    </PropertyRow>
  );

  // Priority always carries a value, so it is never "still missing" — the slot's
  // required flag is honoured as a marker and nothing more.
  const priorityRow = (slot: FormPropertySlot): ReactNode => (
    <PropertyRow label={t('task.createForm.pill.priority')} required={slot.required}>
      <PropertyPill
        variant="row"
        icon={priorityIcon}
        iconClassName={priorityIconClass}
        label={t('task.createForm.pill.priority')}
        value={priorityValue}
        emptyText={notSet}
        disabled={isSubmitting}
        popoverTitle={t('task.field.priority')}
      >
        {(close) => (
          <PillOptionList
            ariaLabel={t('task.field.priority')}
            value={form.priority}
            options={taskPriorityOptions.map((option) => ({
              value: option.value,
              label: t(option.labelKey),
              icon: getPriorityIconPath(option.value),
              iconClassName: getPriorityColor(option.value),
            }))}
            onSelect={(value) => {
              setField('priority', value);
              close();
            }}
          />
        )}
      </PropertyPill>
    </PropertyRow>
  );

  const assigneeRow = (slot: FormPropertySlot): ReactNode => {
    // Two ways to say the same thing: the lifecycle flag the type has always
    // had, and the form slot it can now be marked required in.
    const required = slot.required || capabilities.requiresAssignee;
    return (
    <PropertyRow
      label={t('task.createForm.pill.assignee')}
      required={required}
      error={errorText('assignee')}
      errorId={rowErrorId(slot)}
    >
      <PropertyPill
        variant="row"
        icon={FORM_ICONS.user}
        label={t('task.createForm.pill.assignee')}
        value={form.assigneeName}
        emptyText={notSet}
        required={required}
        invalid={Boolean(errors.assignee)}
        errorId={rowErrorId(slot)}
        disabled={isSubmitting}
        popoverTitle={t('task.field.assignee')}
        popoverWidth={320}
      >
        {() => (
          <>
            <UserPicker
              value={form.assignee}
              onChange={setAssignee}
              placeholder={t('task.field.assigneePlaceholder')}
              scope={pickerScope}
              valueKind="profile"
            />
            {form.assignee && (
              <button
                type="button"
                onClick={() => setAssignee('', '')}
                className={clearButtonClass}
              >
                {t('common.clear')}
              </button>
            )}
          </>
        )}
      </PropertyPill>
    </PropertyRow>
    );
  };

  const dueDateRow = (slot: FormPropertySlot): ReactNode => (
    <PropertyRow
      label={t('task.field.dueDate')}
      required={slot.required}
      error={errorText('due_date')}
      errorId={rowErrorId(slot)}
    >
      <PropertyPill
        variant="row"
        icon={FORM_ICONS.calendar}
        label={t('task.field.dueDate')}
        value={formatDate(form.due_date)}
        emptyText={notSet}
        required={slot.required}
        invalid={Boolean(errors.due_date)}
        errorId={rowErrorId(slot)}
        disabled={isSubmitting}
        popoverTitle={t('task.field.dueDate')}
        popoverWidth={240}
      >
        {() => (
          <div className="flex flex-col gap-2">
            <label htmlFor="task-create-due-date" className="sr-only">
              {t('task.field.dueDate')}
            </label>
            <input
              id="task-create-due-date"
              type="date"
              value={form.due_date}
              onChange={(e) => setField('due_date', e.target.value)}
              className={dateInputClass}
            />
            {form.due_date && (
              <button type="button" onClick={() => setField('due_date', '')} className={clearButtonClass}>
                {t('common.clear')}
              </button>
            )}
          </div>
        )}
      </PropertyPill>
    </PropertyRow>
  );

  const startDateRow = (slot: FormPropertySlot): ReactNode => (
    <PropertyRow
      label={t('task.startDate')}
      required={slot.required}
      error={errorText('start_date')}
      errorId={rowErrorId(slot)}
    >
      <PropertyPill
        variant="row"
        icon={FORM_ICONS.calendar}
        label={t('task.startDate')}
        value={formatDate(form.start_date)}
        emptyText={notSet}
        required={slot.required}
        invalid={Boolean(errors.start_date)}
        errorId={rowErrorId(slot)}
        disabled={isSubmitting}
        popoverTitle={t('task.startDate')}
        popoverWidth={240}
      >
        {() => (
          <div className="flex flex-col gap-2">
            <label htmlFor="task-create-start-date" className="sr-only">
              {t('task.startDate')}
            </label>
            <input
              id="task-create-start-date"
              type="date"
              value={form.start_date}
              onChange={(e) => setField('start_date', e.target.value)}
              className={dateInputClass}
            />
            {form.start_date && (
              <button
                type="button"
                onClick={() => setField('start_date', '')}
                className={clearButtonClass}
              >
                {t('common.clear')}
              </button>
            )}
          </div>
        )}
      </PropertyPill>
    </PropertyRow>
  );

  // Estimate exists only where the type tracks time — a field that would be
  // ignored on save is worse than one that is absent. The lifecycle flag and the
  // form slot are different questions, and this one is settled by the flag.
  const estimateRow = (slot: FormPropertySlot): ReactNode =>
    capabilities.timeTracking && (
    <PropertyRow
      label={t('task.createForm.pill.estimate')}
      required={slot.required}
      error={errorText('estimated_hours')}
      errorId={rowErrorId(slot)}
    >
      <PropertyPill
        variant="row"
        icon={FORM_ICONS.clock}
        label={t('task.createForm.pill.estimate')}
        value={
          form.estimated_hours ? t('task.createForm.hoursShort', { hours: form.estimated_hours }) : ''
        }
        emptyText={notSet}
        required={slot.required}
        invalid={Boolean(errors.estimated_hours)}
        errorId={rowErrorId(slot)}
        disabled={isSubmitting}
        popoverTitle={t('task.estimatedHours')}
        popoverWidth={220}
      >
        {() => (
          <div className="flex flex-col gap-2">
            <label htmlFor="task-create-estimate" className="sr-only">
              {t('task.estimatedHours')}
            </label>
            <input
              id="task-create-estimate"
              type="text"
              inputMode="decimal"
              value={form.estimated_hours}
              onChange={(e) => setField('estimated_hours', e.target.value)}
              placeholder="—"
              className={`${dateInputClass} tabular-nums`}
            />
            {form.estimated_hours && (
              <button
                type="button"
                onClick={() => setField('estimated_hours', '')}
                className={clearButtonClass}
              >
                {t('task.clearEstimatedHours')}
              </button>
            )}
          </div>
        )}
      </PropertyPill>
    </PropertyRow>
  );

  const tagsRow = (slot: FormPropertySlot): ReactNode => (
    <PropertyRow
      label={t('task.createForm.pill.tags')}
      required={slot.required}
      error={errorText('tags')}
      errorId={rowErrorId(slot)}
    >
      <PropertyPill
        variant="row"
        icon={FORM_ICONS.tag}
        label={t('task.createForm.pill.tags')}
        value={tagsValue}
        emptyText={notSet}
        required={slot.required}
        invalid={Boolean(errors.tags)}
        errorId={rowErrorId(slot)}
        disabled={isSubmitting}
        popoverTitle={t('task.field.tags')}
        popoverWidth={320}
      >
        {() => (
          <TagPicker
            value={tags}
            onChange={setTags}
            placeholder={t('tag.selectTags')}
          />
        )}
      </PropertyPill>
    </PropertyRow>
  );

  // Watchers are a panel property now rather than a disclosed section: the type
  // decides whether the form asks for them at all, and a property with two
  // homes is two places to look. The list lives inside the popover, where the
  // picker that adds to it already is.
  const watchersRow = (slot: FormPropertySlot): ReactNode =>
    capabilities.watchers && (
    <PropertyRow
      label={t('task.watchers')}
      required={slot.required}
      error={errorText('watchers')}
      errorId={rowErrorId(slot)}
    >
      <PropertyPill
        variant="row"
        icon={FORM_ICONS.users}
        label={t('task.watchers')}
        value={
          watchers.length === 0
            ? ''
            : watchers.length <= 2
              ? watchers.map((watcher) => watcher.name).join(', ')
              : t('task.createForm.watcherCount', { count: watchers.length })
        }
        emptyText={notSet}
        required={slot.required}
        invalid={Boolean(errors.watchers)}
        errorId={rowErrorId(slot)}
        disabled={isSubmitting}
        popoverTitle={t('task.watchers')}
        popoverHint={t('task.createForm.watchersHint')}
        popoverWidth={320}
      >
        {() => (
          <>
            {watchers.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {watchers.map((watcher) => (
                  <li
                    key={watcher.id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground"
                  >
                    <EntityAvatar name={watcher.name} userId={watcher.id} size="xs" className="h-4 w-4 text-[8px]" />
                    {watcher.name}
                    <button
                      type="button"
                      onClick={() => setWatchers((prev) => prev.filter((w) => w.id !== watcher.id))}
                      aria-label={t('task.createForm.removeWatcher', { name: watcher.name })}
                      className="rounded-sm text-muted-foreground hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Icon d={FORM_ICONS.x} className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <UserPicker
              value=""
              onChange={(userId, displayName) => {
                if (!userId || watchers.some((w) => w.id === userId)) return;
                setWatchers((prev) => [...prev, { id: userId, name: displayName }]);
              }}
              placeholder={t('task.addWatcher')}
              scope={pickerScope}
              valueKind="profile"
            />
          </>
        )}
      </PropertyPill>
    </PropertyRow>
  );

  /** A custom field the type pulled into the panel, rendered as its own row. */
  const customFieldRow = (def: CustomFieldDefinition): ReactNode => {
    const fieldId = customFieldId(def);
    const domId = `task-create-cf-${fieldId}`;
    const message = errorText(fieldId);
    return (
      // The control renders its own message (and marks itself invalid from the
      // same prop), so the row does not repeat it — it only carries the anchor
      // the "scroll to the first problem" pass looks for.
      <div data-field-error={message ? 'true' : undefined}>
        <PropertyRow label={def.display_name} required={def.is_required}>
          <div className="px-0.5">
            <label htmlFor={labelableCustomField(def) ? domId : undefined} className="sr-only">
              {def.display_name}
            </label>
            <CustomFieldControl
              def={def}
              id={domId}
              value={cfValues[fieldId]}
              error={message}
              projectId={parentKind === 'project' ? parentId : undefined}
              teamId={parentKind === 'team' ? parentId : undefined}
              onChange={(value, label) => setCustomField(fieldId, value, label)}
            />
          </div>
        </PropertyRow>
      </div>
    );
  };

  /** One slot → one row. Anything the panel has no control for is left out. */
  const renderSlot = (slot: FormPropertySlot): ReactNode => {
    if (slot.customFieldId) {
      const entry = panelCustomFields.find((candidate) => candidate.slot.key === slot.key);
      // A field the catalogue no longer has is nothing to draw. It stays in the
      // configuration so the administrator can see and remove it there.
      return entry ? customFieldRow(entry.def) : null;
    }
    switch (slot.property) {
      case 'TASK_FORM_PROPERTY_PARENT':
        return parentRow(slot);
      case 'TASK_FORM_PROPERTY_TYPE':
        return typeRow();
      case 'TASK_FORM_PROPERTY_CATEGORY':
        return categoryRow(slot);
      case 'TASK_FORM_PROPERTY_PRIORITY':
        return priorityRow(slot);
      case 'TASK_FORM_PROPERTY_ASSIGNEE':
        return assigneeRow(slot);
      case 'TASK_FORM_PROPERTY_DUE_DATE':
        return dueDateRow(slot);
      case 'TASK_FORM_PROPERTY_START_DATE':
        return startDateRow(slot);
      case 'TASK_FORM_PROPERTY_ESTIMATE':
        return estimateRow(slot);
      case 'TASK_FORM_PROPERTY_TAGS':
        return tagsRow(slot);
      case 'TASK_FORM_PROPERTY_WATCHERS':
        return watchersRow(slot);
      default:
        return null;
    }
  };

  const propertiesPanel: ReactNode = (
    <aside
      aria-labelledby="task-create-properties-heading"
      // Spans every row of the form grid so the alerts and the action bar can
      // sit in the content column instead of running the full page width.
      className="lg:sticky lg:top-4 lg:col-start-2 lg:row-start-1 lg:row-span-3 lg:self-start"
    >
      <h2
        id="task-create-properties-heading"
        className="mb-2 px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground"
      >
        {t('task.createForm.properties')}
      </h2>
      <div className="flex flex-col gap-2.5 rounded-lg border border-border bg-muted/20 p-2">
        {formSlots.map((slot) => (
          <Fragment key={slot.key}>{renderSlot(slot)}</Fragment>
        ))}
      </div>
    </aside>
  );

  const addActionsRow: ReactNode = (
    <div className="flex flex-wrap items-center gap-1 border-t border-border pt-3">
      {capabilities.checklists && (
        <AddAction
          icon={FORM_ICONS.checklist}
          label={t('task.checklists')}
          count={checklistItemCount}
          open={sectionOpen(SECTION_CHECKLISTS, checklists.length > 0)}
          onToggle={() => toggleSection(SECTION_CHECKLISTS, checklists.length > 0)}
          controls={`${idPrefix}-section-checklists`}
          disabled={isSubmitting}
        />
      )}
      {capabilities.watchers && (
        <AddAction
          icon={FORM_ICONS.users}
          label={t('task.watchers')}
          count={watchers.length}
          open={sectionOpen(SECTION_WATCHERS, watchers.length > 0)}
          onToggle={() => toggleSection(SECTION_WATCHERS, watchers.length > 0)}
          controls={`${idPrefix}-section-watchers`}
          disabled={isSubmitting}
        />
      )}
      {/* Files ride along with `CreateTask`; an existing task has no
          field-mask path for them, so they are added where they are read. */}
      {!isEdit && (
        <AddAction
          icon={FORM_ICONS.paperclip}
          label={t('task.attachments')}
          count={pendingFiles.length}
          open={sectionOpen(SECTION_ATTACHMENTS, pendingFiles.length > 0)}
          onToggle={() => toggleSection(SECTION_ATTACHMENTS, pendingFiles.length > 0)}
          controls={`${idPrefix}-section-attachments`}
          disabled={isSubmitting}
        />
      )}
      {customFieldGroups.map((group) => {
        const key = customFieldSectionKey(group);
        const holdsContent =
          filledInGroup(group) > 0 || advancedPreset || group.fields.some((def) => def.is_required);
        return (
          <AddAction
            key={key}
            icon={FORM_ICONS.sliders}
            label={group.key || t('task.customFields')}
            count={filledInGroup(group)}
            open={sectionOpen(key, holdsContent)}
            onToggle={() => toggleSection(key, holdsContent)}
            controls={`${idPrefix}-section-${key}`}
            disabled={isSubmitting}
          />
        );
      })}
      {customFieldsLoading && customFieldGroups.length === 0 && (
        <span className="h-7 w-24 motion-safe:animate-pulse rounded-md bg-muted" aria-busy="true" />
      )}
    </div>
  );

  // What those add-actions opened, inline and in the same order.
  const disclosedSections: ReactNode = (
    <>
      {capabilities.checklists && sectionOpen(SECTION_CHECKLISTS, checklists.length > 0) && (
        <FormSection
          id={`${idPrefix}-section-checklists`}
          title={t('task.checklists')}
          hint={isEdit ? t('task.form.checklistsEditHint') : t('task.createForm.checklistsHint')}
        >
          <ChecklistDraftEditor checklists={checklists} onChange={setChecklists} />
        </FormSection>
      )}

      {capabilities.watchers && sectionOpen(SECTION_WATCHERS, watchers.length > 0) && (
        <FormSection
          id={`${idPrefix}-section-watchers`}
          title={t('task.watchers')}
          hint={t('task.createForm.watchersHint')}
        >
          {watchers.length > 0 && (
            <ul className="mb-2 flex flex-wrap gap-1.5">
              {watchers.map((watcher) => (
                <li
                  key={watcher.id}
                  className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground"
                >
                  <EntityAvatar name={watcher.name} userId={watcher.id} size="xs" className="h-4 w-4 text-[8px]" />
                  {watcher.name}
                  <button
                    type="button"
                    onClick={() => setWatchers((prev) => prev.filter((w) => w.id !== watcher.id))}
                    aria-label={t('task.createForm.removeWatcher', { name: watcher.name })}
                    className="rounded-sm text-muted-foreground hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Icon d={FORM_ICONS.x} className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <UserPicker
            value=""
            onChange={(userId, displayName) => {
              if (!userId || watchers.some((w) => w.id === userId)) return;
              setWatchers((prev) => [...prev, { id: userId, name: displayName }]);
            }}
            placeholder={t('task.addWatcher')}
            scope={pickerScope}
            valueKind="profile"
          />
        </FormSection>
      )}

      {!isEdit && sectionOpen(SECTION_ATTACHMENTS, pendingFiles.length > 0) && (
        <FormSection id={`${idPrefix}-section-attachments`} title={t('task.attachments')}>
          <AttachmentDropzone
            files={pendingFiles}
            onAdd={handleAddFiles}
            onRemove={(id) => setPendingFiles((prev) => prev.filter((file) => file.id !== id))}
            disabled={isSubmitting}
          />
        </FormSection>
      )}

      {/* Custom fields — grouped by `group`, ordered by `position`. */}
      {customFieldGroups.map((group) => {
        const key = customFieldSectionKey(group);
        const holdsContent =
          filledInGroup(group) > 0 || advancedPreset || group.fields.some((def) => def.is_required);
        if (!sectionOpen(key, holdsContent)) return null;
        return (
          <FormSection
            key={key}
            id={`${idPrefix}-section-${key}`}
            title={group.key || t('task.customFields')}
            aside={
              <Link
                to="/tasks/settings"
                target="_blank"
                className="shrink-0 rounded-sm text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('common.manage')}
              </Link>
            }
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {group.fields.map((def) => {
                const fieldId = customFieldId(def);
                const domId = `${idPrefix}-cf-${fieldId}`;
                const message = errorText(fieldId);
                return (
                  <div key={fieldId} data-field-error={message ? 'true' : undefined}>
                    <label
                      htmlFor={labelableCustomField(def) ? domId : undefined}
                      className="mb-1 block text-xs text-muted-foreground"
                    >
                      {def.display_name}
                      {def.is_required && (
                        <span className="ml-1 text-destructive" aria-hidden="true">
                          *
                        </span>
                      )}
                    </label>
                    <CustomFieldControl
                      def={def}
                      id={domId}
                      value={cfValues[fieldId]}
                      error={message}
                      projectId={parentKind === 'project' ? parentId : undefined}
                      teamId={parentKind === 'team' ? parentId : undefined}
                      onChange={(value, label) => setCustomField(fieldId, value, label)}
                    />
                  </div>
                );
              })}
            </div>
          </FormSection>
        );
      })}
    </>
  );

  // Anything that went wrong, or that will not survive the chosen path, kept
  // beside the button that acts on it.
  const alerts: ReactNode = (contextError ||
    uploadWarning ||
    typeDropped.length > 0 ||
    templateDropped.length > 0 ||
    !!store.error) && (
    <div className="space-y-2">
      {contextError && (
        <Alert variant="warning">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex-1">{t('task.createForm.contextDegraded')}</span>
            <button
              type="button"
              onClick={() => retryContext()}
              className="rounded-md px-2 py-1 text-sm font-medium underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('common.retry')}
            </button>
          </div>
        </Alert>
      )}
      {/* A type change rewrites the form. What the new type does not ask for
          is named here — the two modes lose it differently, and say so. */}
      {typeDropped.length > 0 && (
        <Alert variant="warning">
          {t(isEdit ? 'task.form.typeDropsEdit' : 'task.form.typeDropsCreate', {
            type: typeLabel(selectedType),
            fields: typeDropped.join(', '),
          })}
        </Alert>
      )}
      {templateDropped.length > 0 && (
        <Alert variant="warning">
          {t('task.createForm.templateDrops', { fields: templateDropped.join(', ') })}
        </Alert>
      )}
      {uploadWarning && <Alert variant="warning">{t('task.createForm.attachmentsFailed')}</Alert>}
      {!!store.error && (
        <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
      )}
    </div>
  );

  return (
    <form
      ref={formRef}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="mt-4 space-y-5"
    >
      {/* Two columns: what you write, and what you choose. Below `lg` the
          panel stacks under the composer rather than squeezing beside it. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-8">
        <div className="min-w-0 space-y-5">
          {titleBlock}
          {descriptionBlock}
          {addActionsRow}
          {disclosedSections}
        </div>
        {propertiesPanel}
      </div>

      {alerts}

      {actions}
    </form>
  );
}
